import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'

import {
  api,
  can,
  columnTypes,
  isActive,
  templateMethods,
  type ExtractColumn,
  type ExtractionSpec,
  type GridSheet,
  type SkipRule,
  type SourceInput,
} from '../api'
import { ErrorBox, Pill, RunStatus } from '../components'
import { bytes, dt, num } from '../format'
import { gridTheme } from '../grid'
import { pollWhile } from '../hooks'

// Extraction: sources read with templates (uploads, HTTP APIs, Google
// Drive). The editor shows a stored file as a grid; the editor drags the
// data range, marks header rows, maps headers to typed columns and sees the
// output live. Saving stores a new template version; a run re-extracts.

export function Extraction() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const sources = useQuery({ queryKey: ['sources'], queryFn: api.sources })
  const [creating, setCreating] = useState(false)
  const list = sources.data?.filter((s) => templateMethods.includes(s.access_method)) ?? []
  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Extraction</h1>
          <p className="sub">Uploaded files, HTTP APIs and Google Drive files, turned into typed tables with versioned templates.</p>
        </div>
        {can(me.data, 'editor') && (
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New source'}
          </button>
        )}
      </header>
      {creating && <NewSourceForm />}
      {sources.error && <ErrorBox error={sources.error} />}
      {list.length === 0 && !creating && <p className="muted">No extraction sources yet.</p>}
      {list.length > 0 && (
        <table className="tbl card">
          <thead>
            <tr><th>Source</th><th>Access</th><th>Status</th><th>Last run</th><th /></tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.slug}>
                <td>
                  <Link to={`/sources/${s.slug}`}>{s.name}</Link> <span className="muted mono">{s.slug}</span>
                </td>
                <td className="mono">{s.access_method}</td>
                <td><Pill kind={s.status === 'active' ? 'ok' : 'warn'}>{s.status}</Pill></td>
                <td>{s.active_run ? <RunStatus run={s.active_run} /> : `${dt(s.last_run_at)} ${s.last_run_status ?? ''}`}</td>
                <td><Link className="btn btn-sm" to={`/extraction/${s.slug}`}>Open editor</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function NewSourceForm() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const its = useQuery({ queryKey: ['integrations'], queryFn: api.integrations })
  const [f, setF] = useState({ name: '', slug: '', publisher: '', url: 'https://', method: 'upload', usage: 'export', integration: '', path: '/', http_method: 'GET', body: '', file_id: '', export: 'xlsx', schedule: '' })
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }))
  const slug = f.slug || slugify(f.name, '-')
  const options = its.data?.integrations.filter((i) => i.kind === f.method) ?? []
  const save = useMutation({
    mutationFn: () => {
      const input: SourceInput = { slug, name: f.name, publisher: f.publisher, url: f.url, access_method: f.method, usage_mode: f.usage, schedule: f.schedule || null }
      if (f.method === 'http') input.config = { integration: f.integration, path: f.path, ...(f.http_method === 'POST' && { method: 'POST', body: f.body }) }
      if (f.method === 'google_drive') input.config = { integration: f.integration, file_id: f.file_id, export: f.export }
      return api.createSource(input)
    },
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['sources'] })
      navigate(`/extraction/${s.slug}`)
    },
  })
  return (
    <form className="card form" onSubmit={(e) => { e.preventDefault(); save.mutate() }}>
      <h2>New extraction source</h2>
      <div className="form-grid">
        <label>Name<input value={f.name} onChange={(e) => set('name')(e.target.value)} required /></label>
        <label>Slug<input value={f.slug} placeholder={slug} onChange={(e) => set('slug')(e.target.value)} /></label>
        <label>Publisher<input value={f.publisher} onChange={(e) => set('publisher')(e.target.value)} required /></label>
        <label>Publisher URL<input value={f.url} onChange={(e) => set('url')(e.target.value)} required /></label>
        <label>Access
          <select value={f.method} onChange={(e) => set('method')(e.target.value)}>
            <option value="upload">Uploaded files</option>
            <option value="http">HTTP API (integration)</option>
            <option value="google_drive">Google Drive file (integration)</option>
          </select>
        </label>
        <label>Usage mode
          <select value={f.usage} onChange={(e) => set('usage')(e.target.value)}>
            <option>export</option><option>cache</option><option>proxy_only</option>
          </select>
        </label>
        {f.method !== 'upload' && (
          <>
            <label>Integration
              <select value={f.integration} onChange={(e) => set('integration')(e.target.value)} required>
                <option value="">choose…</option>
                {options.map((i) => <option key={i.slug} value={i.slug}>{i.name}{i.enabled ? '' : ' (disabled)'}</option>)}
              </select>
            </label>
            <label>Schedule (cron, optional)<input className="mono" value={f.schedule} placeholder="0 6 * * *" onChange={(e) => set('schedule')(e.target.value)} /></label>
          </>
        )}
        {f.method === 'http' && <label className="wide">Path under the integration’s base URL<input className="mono" value={f.path} onChange={(e) => set('path')(e.target.value)} required /></label>}
        {f.method === 'http' && (
          <label>Request
            <select value={f.http_method} onChange={(e) => set('http_method')(e.target.value)}>
              <option value="GET">GET</option>
              <option value="POST">POST with a body</option>
            </select>
          </label>
        )}
        {f.method === 'http' && f.http_method === 'POST' && (
          <label className="wide">Body (an API-key-in-body integration replaces {'{{secret}}'})
            <textarea rows={5} className="mono" value={f.body} spellCheck={false} onChange={(e) => set('body')(e.target.value)} required />
          </label>
        )}
        {f.method === 'google_drive' && (
          <>
            <label>File id (from the file’s URL)<input className="mono" value={f.file_id} onChange={(e) => set('file_id')(e.target.value)} required /></label>
            <label>Google Sheets export as
              <select value={f.export} onChange={(e) => set('export')(e.target.value)}><option>xlsx</option><option>csv</option></select>
            </label>
          </>
        )}
      </div>
      {f.method !== 'upload' && options.length === 0 && <p className="callout warn">No {f.method} integration exists yet. An admin sets one up under <Link to="/integrations">Integrations</Link>.</p>}
      {save.error && <ErrorBox error={save.error} />}
      <div className="row">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>Create source</button>
        <span className="note">The source starts as a draft; record its licence on the source page before publishing anything built from it.</span>
      </div>
    </form>
  )
}

// Helpers ---------------------------------------------------------------------

const slugify = (s: string, sep: '-' | '_') => {
  const out = s
    .toLowerCase()
    .replace(/å|ä/g, 'a')
    .replace(/ö/g, 'o')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, sep)
    .replace(new RegExp(`^\\${sep}+|\\${sep}+$`, 'g'), '')
  return out
}

const colLetter = (i: number) => {
  let s = ''
  for (i++; i > 0; i = Math.floor((i - 1) / 26)) s = String.fromCharCode(65 + ((i - 1) % 26)) + s
  return s
}
const colIndex = (letters: string) => [...letters].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0) - 1

interface Rect { r0: number; c0: number; r1: number; c1: number | null }

/** Parses A3:F or A3:F200 (rows to the end when the second row is empty). */
function parseRange(r: string | undefined): Rect | null {
  const m = /^([A-Z]{1,3})([0-9]+):([A-Z]{1,3})([0-9]*)$/.exec(r ?? '')
  if (!m) return null
  return { r0: Number(m[2]) - 1, c0: colIndex(m[1]), r1: m[4] ? Number(m[4]) - 1 : Infinity, c1: colIndex(m[3]) }
}

function formatRange(a: { r: number; c: number }, b: { r: number; c: number }, toEnd: boolean) {
  const r0 = Math.min(a.r, b.r), r1 = Math.max(a.r, b.r)
  const c0 = Math.min(a.c, b.c), c1 = Math.max(a.c, b.c)
  return `${colLetter(c0)}${r0 + 1}:${colLetter(c1)}${toEnd ? '' : r1 + 1}`
}

/** Header texts as the server joins them (for proposing columns). */
function headersOf(sheet: GridSheet | undefined, spec: ExtractionSpec): string[] {
  const cells = sheet?.cells ?? []
  const rg = parseRange(spec.range) ?? { r0: 0, c0: 0, r1: Infinity, c1: Math.max(0, sheet ? sheet.cols - 1 : 0) }
  const c1 = rg.c1 ?? 0
  const out: string[] = []
  for (let c = rg.c0; c <= c1; c++) {
    const parts: string[] = []
    for (const h of spec.header_rows) {
      const v = (cells[rg.r0 + h]?.[c] ?? '').split(/\s+/).filter(Boolean).join(' ')
      if (v && parts[parts.length - 1] !== v) parts.push(v)
    }
    out.push(parts.join(spec.header_joiner) || `#${c - rg.c0}`)
  }
  return out
}

function guessType(values: string[]): string {
  const vs = values.map((v) => v.trim()).filter((v) => v && !['-', '..', 'n/a'].includes(v))
  if (vs.length === 0) return 'VARCHAR'
  if (vs.every((v) => /^\d{4}-\d{2}-\d{2}$/.test(v))) return 'DATE'
  if (vs.every((v) => /^-?[\d\s ]+$/.test(v))) return 'BIGINT'
  if (vs.every((v) => /^-?[\d\s .,]+%?$/.test(v) || /^-?\d+(\.\d+)?(E-?\d+)?$/i.test(v))) return 'DOUBLE'
  if (vs.every((v) => /^(true|false|ja|nej|yes|no)$/i.test(v))) return 'BOOLEAN'
  return 'VARCHAR'
}

function blankSpec(source: string, format: ExtractionSpec['format']): ExtractionSpec {
  return {
    format, source, output_table: slugify(source, '_'), header_rows: [0], header_joiner: ' / ', orientation: 'vertical',
    fill_merged: true, null_markers: ['', '-', '..', 'n/a'], csv_encoding: 'utf-8', columns: [], max_cast_failures: 0.01, skip: [],
  }
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

// Editor ----------------------------------------------------------------------

export function ExtractionEditor() {
  const { source = '' } = useParams()
  const [params] = useSearchParams()
  const files = useQuery({ queryKey: ['files', source], queryFn: () => api.files(source) })
  const templates = useQuery({ queryKey: ['templates', source], queryFn: () => api.sourceTemplates(source) })
  if (templates.error) return <ErrorBox error={templates.error} />
  if (!templates.data || !files.data) return <p className="muted">Loading…</p>
  const tplSlug = params.get('template') ?? templates.data[0]?.slug ?? ''
  const t = templates.data.find((x) => x.slug === tplSlug)
  const fmt = (files.data[0]?.original_name ?? files.data[0]?.key ?? '').split('.').pop()?.toLowerCase() ?? ''
  const initial = t ? t.spec : blankSpec(source, (['xlsx', 'csv', 'json', 'xml'].includes(fmt) ? fmt : 'xlsx') as ExtractionSpec['format'])
  // Remount per template, so its saved spec is the editor's initial state.
  return <EditorBody key={`${source}/${tplSlug}`} source={source} tplSlug={tplSlug} initial={initial} />
}

function EditorBody({ source, tplSlug, initial }: { source: string; tplSlug: string; initial: ExtractionSpec }) {
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const src = useQuery({ queryKey: ['source', source], queryFn: () => api.source(source), refetchInterval: (q) => pollWhile(!!q.state.data?.runs.some(isActive)) })
  const files = useQuery({ queryKey: ['files', source], queryFn: () => api.files(source) })
  const templates = useQuery({ queryKey: ['templates', source], queryFn: () => api.sourceTemplates(source) })
  const editor = can(me.data, 'editor')

  const fileId = Number(params.get('file') ?? 0)
  const [spec, setSpec] = useState<ExtractionSpec>(initial)
  const [comment, setComment] = useState('')

  const grid = useQuery({
    queryKey: ['grid', source, fileId, spec.sheet, spec.format, spec.csv_delimiter, spec.csv_encoding, spec.record_path],
    queryFn: () => api.grid(source, fileId, { sheet: spec.sheet, format: spec.format, csv_delimiter: spec.csv_delimiter || undefined, csv_encoding: spec.csv_encoding, record_path: spec.record_path || undefined }),
    enabled: (files.data?.length ?? 0) > 0,
    retry: false,
  })
  const sheet = grid.data?.sheets[grid.data.sheet]

  const debounced = useDebounced(spec, 400)
  const preview = useQuery({
    queryKey: ['preview', fileId, JSON.stringify(debounced)],
    queryFn: () => api.previewTemplate(debounced, fileId),
    enabled: debounced.columns.length > 0 && (files.data?.length ?? 0) > 0,
    retry: false,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['files', source] })
    qc.invalidateQueries({ queryKey: ['source', source] })
    qc.invalidateQueries({ queryKey: ['templates', source] })
  }
  const uploadM = useMutation({ mutationFn: (f: File) => api.uploadFile(source, f), onSuccess: () => { setParams((p) => { p.delete('file'); return p }); invalidate() } })
  const fetchM = useMutation({ mutationFn: () => api.triggerRun(source), onSuccess: invalidate })
  const extractM = useMutation({ mutationFn: () => api.triggerExtract(source), onSuccess: invalidate })
  const saveM = useMutation({
    mutationFn: (body: ExtractionSpec) => api.saveTemplate(body, comment),
    onSuccess: (t) => {
      setComment('')
      setSpec(t.spec)
      setParams((p) => { p.set('template', t.slug); return p })
      invalidate()
    },
  })

  if (src.error) return <ErrorBox error={src.error} />
  if (!src.data) return <p className="muted">Loading…</p>
  const s = src.data
  const update = (patch: Partial<ExtractionSpec>) => setSpec((x) => ({ ...x, ...patch }))
  const active = s.runs.find(isActive)
  const p = preview.data
  const fpChanged = p && spec.expected_fingerprint && spec.expected_fingerprint !== p.header_fingerprint

  return (
    <section className="page">
      <p className="crumbs">
        <Link to="/extraction">Extraction</Link> / <Link to={`/sources/${s.slug}`} className="mono">{s.slug}</Link>
      </p>
      <header className="page-h">
        <div>
          <h1>{s.name}</h1>
          <p className="sub">{s.access_method === 'upload' ? 'Uploaded files' : s.access_method === 'http' ? `HTTP via ${String(s.config.integration)} ${s.config.method === 'POST' ? 'POST ' : ''}${String(s.config.path ?? '')}` : `Google Drive via ${String(s.config.integration)}`}</p>
        </div>
        {editor && (
          <div className="row">
            {s.access_method === 'upload' ? (
              <label className="btn btn-primary">
                {uploadM.isPending ? 'Uploading…' : 'Upload file'}
                <input type="file" hidden accept=".xlsx,.csv,.tsv,.json,.xml" onChange={(e) => e.target.files?.[0] && uploadM.mutate(e.target.files[0])} />
              </label>
            ) : (
              <button className="btn btn-primary" disabled={!!active || fetchM.isPending} onClick={() => fetchM.mutate()}>Fetch now</button>
            )}
            <button className="btn" disabled={!!active || extractM.isPending || templates.data?.length === 0} onClick={() => extractM.mutate()} title="Apply the saved templates to the newest file">
              Re-extract
            </button>
          </div>
        )}
      </header>
      {[uploadM.error, fetchM.error, extractM.error].map((e, i) => e && <ErrorBox key={i} error={e} />)}
      {uploadM.data?.unchanged && <p className="callout warn">That file is identical to the newest one; nothing was stored.</p>}
      {active && <p className="progress"><span className="dot" /> <Link to={`/runs/${active.id}`}>Run #{active.id}</Link> <RunStatus run={active} link={false} /></p>}
      {s.runs[0]?.status === 'failed' && !active && (
        <div className="callout err">
          Last run failed: {s.runs[0].error} <Link to={`/runs/${s.runs[0].id}`}>details</Link>
        </div>
      )}

      <div className="card">
        <div className="row-between">
          <h2>Template</h2>
          <div className="row">
            <select aria-label="Template" value={tplSlug} onChange={(e) => setParams((p) => { p.set('template', e.target.value); return p })}>
              {templates.data?.map((t) => <option key={t.slug} value={t.slug}>{t.slug} (v{t.version})</option>)}
              <option value="">new template…</option>
            </select>
            <select aria-label="File" value={fileId} onChange={(e) => setParams((p) => { p.set('file', e.target.value); return p })}>
              <option value={0}>newest file</option>
              {files.data?.map((f) => <option key={f.id} value={f.id}>{f.original_name ?? f.key} · {dt(f.fetched_at)} · {bytes(f.bytes)}</option>)}
            </select>
          </div>
        </div>
        <div className="form form-grid ext-opts">
          <label>Output table<input className="mono" value={spec.output_table} onChange={(e) => update({ output_table: e.target.value })} disabled={!!templates.data?.some((t) => t.slug === tplSlug)} /></label>
          <label>Format
            <select value={spec.format} onChange={(e) => update({ format: e.target.value as ExtractionSpec['format'] })}>
              <option>xlsx</option><option>csv</option><option>json</option><option>xml</option>
            </select>
          </label>
          {spec.format === 'xlsx' && grid.data && (
            <label>Sheet
              <select value={grid.data.sheets[grid.data.sheet].name} onChange={(e) => update({ sheet: e.target.value })}>
                {grid.data.sheets.map((sh) => <option key={sh.name} value={sh.name}>{sh.name} ({sh.rows}×{sh.cols})</option>)}
              </select>
            </label>
          )}
          {spec.format === 'csv' && (
            <>
              <label>Delimiter<select value={spec.csv_delimiter ?? ''} onChange={(e) => update({ csv_delimiter: e.target.value || undefined })}><option value="">detect</option><option value=",">,</option><option value=";">;</option><option value="\t">tab</option><option value="|">|</option></select></label>
              <label>Encoding<select value={spec.csv_encoding} onChange={(e) => update({ csv_encoding: e.target.value })}>{['utf-8', 'latin1', 'windows-1252', 'iso-8859-15', 'utf-16'].map((x) => <option key={x}>{x}</option>)}</select></label>
            </>
          )}
          {(spec.format === 'json' || spec.format === 'xml') && (
            <label>Record path<input className="mono" value={spec.record_path ?? ''} placeholder={spec.format === 'json' ? '/data/items' : '/feed/entry'} onChange={(e) => update({ record_path: e.target.value || undefined })} /></label>
          )}
          <label>Range<input className="mono" value={spec.range ?? ''} placeholder="whole sheet" onChange={(e) => update({ range: e.target.value.toUpperCase() || undefined })} /></label>
          <label>Header rows (in the range)<input className="mono" value={spec.header_rows.join(',')} onChange={(e) => update({ header_rows: e.target.value.split(',').map((x) => x.trim()).filter(Boolean).map(Number).filter((n) => !isNaN(n)) })} /></label>
          <label>Header joiner<input className="mono" value={spec.header_joiner} onChange={(e) => update({ header_joiner: e.target.value })} /></label>
          <label>Orientation<select value={spec.orientation} onChange={(e) => update({ orientation: e.target.value as ExtractionSpec['orientation'] })}><option value="vertical">records are rows</option><option value="horizontal">records are columns</option></select></label>
          <label>Null markers<input className="mono" value={spec.null_markers.map((m) => (m === '' ? '""' : m)).join(' ')} onChange={(e) => update({ null_markers: e.target.value.split(/\s+/).filter(Boolean).map((m) => (m === '""' ? '' : m)) })} /></label>
          <label>Bad cells allowed per column<input inputMode="decimal" value={String(spec.max_cast_failures)} onChange={(e) => update({ max_cast_failures: Number(e.target.value) || 0 })} /></label>
          <label className="check"><input type="checkbox" checked={spec.fill_merged} onChange={(e) => update({ fill_merged: e.target.checked })} />Fill merged cells</label>
        </div>
        <SkipRules rules={spec.skip ?? []} onChange={(skip) => update({ skip })} />
      </div>

      <div className="card">
        <div className="row-between">
          <h2>File {grid.data && <span className="muted mono">{grid.data.format} · {sheet?.rows} rows × {sheet?.cols} columns</span>}</h2>
          <span className="note">Drag to select the data range (Shift: to the last row). Click a row number to toggle it as a header row.</span>
        </div>
        {files.data?.length === 0 && <p className="muted">No file stored yet. {s.access_method === 'upload' ? 'Upload one.' : 'Fetch one.'}</p>}
        {grid.error && <ErrorBox error={grid.error} />}
        {sheet?.cells && <SheetGrid sheet={sheet} spec={spec} onRange={(range) => update({ range })} onHeaderRows={(header_rows) => update({ header_rows })} />}
      </div>

      <ColumnsEditor spec={spec} sheet={sheet} onChange={(columns) => update({ columns })} />

      <div className="card">
        <div className="row-between">
          <h2>Output preview</h2>
          {p && <span className="muted mono">{num(p.rows_in)} rows · {num(p.skipped)} skipped · header {p.header_fingerprint.slice(0, 12)}</span>}
        </div>
        {spec.columns.length === 0 && <p className="muted">Add columns to see the output.</p>}
        {preview.error && <ErrorBox error={preview.error} />}
        {p?.problems?.map((x) => <p key={x} className="callout err">{x}</p>)}
        {fpChanged && (
          <p className="callout warn">
            The header layout differs from the one this template was saved for. Check the columns, then accept the new layout.
            {p?.fingerprint_diff?.missing && ` Missing: ${p.fingerprint_diff.missing.join(', ')}.`}{' '}
            <button className="btn btn-sm" onClick={() => update({ expected_fingerprint: p!.header_fingerprint })}>Accept layout</button>
          </p>
        )}
        {p && (
          <table className="tbl">
            <thead><tr><th>Column</th><th>From</th><th>Type</th><th className="num">Nulls</th><th className="num">Not parsed</th></tr></thead>
            <tbody>
              {p.columns.map((c) => (
                <tr key={c.name}>
                  <td className="mono">{c.name}</td>
                  <td>{c.source_index < 0 ? <span className="err-t">not found</span> : c.header}</td>
                  <td className="mono">{c.type}</td>
                  <td className="num">{num(c.nulls)}</td>
                  <td className="num">{c.cast_failed > 0 ? <span className="err-t" title={c.cast_failed_examples?.join(' | ')}>{num(c.cast_failed)}</span> : 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {p && p.rows.length > 0 && <PreviewRows rows={p.rows} columns={spec.columns} />}
      </div>

      {editor && (
        <div className="card">
          <h2>Save</h2>
          <div className="row">
            <input className="grow" placeholder="What changed and why" value={comment} onChange={(e) => setComment(e.target.value)} />
            <button
              className="btn btn-primary"
              disabled={saveM.isPending || spec.columns.length === 0}
              onClick={() => {
                // A new template records the layout it was made for.
                const body = !spec.expected_fingerprint && p ? { ...spec, expected_fingerprint: p.header_fingerprint } : spec
                setSpec(body)
                saveM.mutate(body)
              }}
            >
              {saveM.isPending ? 'Saving…' : 'Save as new version'}
            </button>
          </div>
          {!spec.expected_fingerprint && p && <p className="note">Saving records the current header layout ({p.header_fingerprint.slice(0, 12)}); a different layout later fails the run instead of loading wrong data.</p>}
          {saveM.error && <ErrorBox error={saveM.error} />}
          {saveM.data && <p className="callout ok">Saved {saveM.data.slug} v{saveM.data.version}. Use Re-extract to apply it to the stored file.</p>}
        </div>
      )}
    </section>
  )
}

function SkipRules({ rules, onChange }: { rules: SkipRule[]; onChange: (r: SkipRule[]) => void }) {
  const set = (i: number, r: SkipRule) => onChange(rules.map((x, j) => (j === i ? r : x)))
  return (
    <div className="skip">
      <span className="note">Skip rows</span>
      {rules.map((r, i) => (
        <span key={i} className="row">
          <select value={r.kind} onChange={(e) => set(i, { kind: e.target.value as SkipRule['kind'] })}>
            <option value="blank">blank rows</option>
            <option value="first_cell_matches">first cell matches</option>
            <option value="any_cell_matches">any cell matches</option>
            <option value="row_index">row numbers (in the range)</option>
          </select>
          {(r.kind === 'first_cell_matches' || r.kind === 'any_cell_matches') && (
            <input className="mono" placeholder="^(Totalt|Källa)" value={r.pattern ?? ''} onChange={(e) => set(i, { ...r, pattern: e.target.value })} />
          )}
          {r.kind === 'row_index' && (
            <input className="mono" placeholder="0,1" value={(r.rows ?? []).join(',')} onChange={(e) => set(i, { ...r, rows: e.target.value.split(',').map(Number).filter((n) => !isNaN(n)) })} />
          )}
          <button className="btn btn-sm" onClick={() => onChange(rules.filter((_, j) => j !== i))}>Remove</button>
        </span>
      ))}
      <button className="btn btn-sm" onClick={() => onChange([...rules, { kind: 'blank' }])}>Add rule</button>
    </div>
  )
}

function SheetGrid({ sheet, spec, onRange, onHeaderRows }: { sheet: GridSheet; spec: ExtractionSpec; onRange: (r: string) => void; onHeaderRows: (h: number[]) => void }) {
  const cells = sheet.cells ?? []
  const width = Math.min(sheet.cols, 50)
  const rg = parseRange(spec.range)
  const [drag, setDrag] = useState<{ a: { r: number; c: number }; b: { r: number; c: number } } | null>(null)
  const merged = useMemo(() => {
    const m = new Set<string>()
    for (const x of sheet.merges ?? []) for (let r = x.from_row; r <= x.to_row; r++) for (let c = x.from_col; c <= x.to_col; c++) m.add(`${r}:${c}`)
    return m
  }, [sheet.merges])
  const r0 = rg?.r0 ?? 0
  const inRange = (r: number, c: number) => {
    if (drag) {
      const [ra, rb] = [Math.min(drag.a.r, drag.b.r), Math.max(drag.a.r, drag.b.r)]
      const [ca, cb] = [Math.min(drag.a.c, drag.b.c), Math.max(drag.a.c, drag.b.c)]
      return r >= ra && r <= rb && c >= ca && c <= cb
    }
    return !!rg && r >= rg.r0 && r <= rg.r1 && c >= rg.c0 && c <= (rg.c1 ?? Infinity)
  }
  const isHeader = (r: number) => spec.header_rows.includes(r - r0) && (!rg || (r >= rg.r0 && r <= rg.r1))
  return (
    <div className="sheet" onMouseLeave={() => setDrag(null)}>
      <table>
        <thead>
          <tr>
            <th />
            {Array.from({ length: width }, (_, c) => <th key={c}>{colLetter(c)}</th>)}
          </tr>
        </thead>
        <tbody>
          {cells.map((row, r) => (
            <tr key={r} className={isHeader(r) ? 'hdr' : undefined}>
              <th
                title="Toggle header row"
                onClick={() => {
                  const rel = r - r0
                  if (rel < 0) return
                  onHeaderRows(spec.header_rows.includes(rel) ? spec.header_rows.filter((h) => h !== rel) : [...spec.header_rows, rel].sort((a, b) => a - b))
                }}
              >
                {r + 1}
              </th>
              {Array.from({ length: width }, (_, c) => (
                <td
                  key={c}
                  className={`${inRange(r, c) ? 'sel' : ''} ${merged.has(`${r}:${c}`) ? 'merged' : ''}`}
                  title={row[c]}
                  onMouseDown={(e) => { e.preventDefault(); setDrag({ a: { r, c }, b: { r, c } }) }}
                  onMouseEnter={() => drag && setDrag({ ...drag, b: { r, c } })}
                  onMouseUp={(e) => {
                    if (!drag) return
                    onRange(formatRange(drag.a, { r, c }, e.shiftKey || Math.max(drag.a.r, r) === cells.length - 1))
                    setDrag(null)
                  }}
                >
                  {row[c] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {sheet.rows > cells.length && <p className="note">Showing the first {cells.length} of {num(sheet.rows)} rows.</p>}
    </div>
  )
}

function ColumnsEditor({ spec, sheet, onChange }: { spec: ExtractionSpec; sheet: GridSheet | undefined; onChange: (c: ExtractColumn[]) => void }) {
  const headers = headersOf(sheet, spec)
  const set = (i: number, c: Partial<ExtractColumn>) => onChange(spec.columns.map((x, j) => (j === i ? { ...x, ...c } : x)))
  const fromHeaders = () => {
    const rg = parseRange(spec.range) ?? { r0: 0, c0: 0 }
    const first = rg.r0 + Math.max(-1, ...spec.header_rows) + 1
    const used = new Set<string>()
    onChange(
      headers.map((h, i) => {
        let name = slugify(h, '_') || `col_${i}`
        if (/^[0-9]/.test(name)) name = `c_${name}`
        name = name.slice(0, 60)
        while (used.has(name)) name += '_'
        used.add(name)
        const sample = (sheet?.cells ?? []).slice(first, first + 50).map((row) => row[rg.c0 + i] ?? '')
        return { source: h, name, type: spec.orientation === 'vertical' ? guessType(sample) : 'VARCHAR' }
      }),
    )
  }
  return (
    <div className="card">
      <div className="row-between">
        <h2>Columns</h2>
        <div className="row">
          <button className="btn btn-sm" onClick={fromHeaders} disabled={headers.length === 0}>{spec.columns.length ? 'Replace with all headers' : 'Add all headers'}</button>
          <button className="btn btn-sm" onClick={() => onChange([...spec.columns, { source: headers[0] ?? '', name: `col_${spec.columns.length}`, type: 'VARCHAR' }])}>Add column</button>
        </div>
      </div>
      <p className="note">Source is the joined header text, a column letter, or #n (position in the range). Names are the output table’s columns.</p>
      {spec.columns.length > 0 && (
        <table className="tbl">
          <thead><tr><th>Source</th><th>Name</th><th>Type</th><th>Extra null markers</th><th /></tr></thead>
          <tbody>
            {spec.columns.map((c, i) => (
              <tr key={i}>
                <td>
                  <input list="ext-headers" value={c.source} onChange={(e) => set(i, { source: e.target.value })} />
                </td>
                <td><input className="mono" value={c.name} onChange={(e) => set(i, { name: e.target.value })} /></td>
                <td><select value={c.type} onChange={(e) => set(i, { type: e.target.value })}>{columnTypes.map((t) => <option key={t}>{t}</option>)}</select></td>
                <td><input className="mono" value={(c.nulls ?? []).join(' ')} onChange={(e) => set(i, { nulls: e.target.value.split(/\s+/).filter(Boolean) })} /></td>
                <td className="row">
                  <button className="btn btn-sm" disabled={i === 0} onClick={() => { const n = [...spec.columns]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; onChange(n) }}>↑</button>
                  <button className="btn btn-sm" onClick={() => onChange(spec.columns.filter((_, j) => j !== i))}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <datalist id="ext-headers">{headers.map((h) => <option key={h} value={h} />)}</datalist>
    </div>
  )
}

function PreviewRows({ rows, columns }: { rows: Record<string, unknown>[]; columns: ExtractColumn[] }) {
  const cols = useMemo<ColDef[]>(
    () => columns.map((c) => ({ field: c.name, headerName: `${c.name} · ${c.type}`, minWidth: 120, flex: 1, valueFormatter: (p: { value: unknown }) => (p.value === null || p.value === undefined ? 'NULL' : typeof p.value === 'object' ? JSON.stringify(p.value) : String(p.value)) })),
    [columns],
  )
  return (
    <div className="grid-wrap" style={{ height: Math.min(460, 56 + rows.length * 36) }}>
      <AgGridReact theme={gridTheme} rowData={rows} columnDefs={cols} rowHeight={36} />
    </div>
  )
}
